import React, { useState, useEffect } from 'react';
import { Radio, Table, Input, Button, Modal, Popconfirm, Tooltip, message } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { controller } from '../controller';
import { INVITES_NONE, INVITES_OFFICES_ONLY, invitesMode } from '../lib/onboardingPlan';

// icon
import add2 from '../../assets/icons/add-circle2.png';


const InviteUsers = (props) => {
    // Holds the office's single subscription tier id — resolved automatically,
    // not chosen per user. Every invited user is covered by this one tier.
    const [officeTierId, setOfficeTierId] = useState(null);
    const [invitedUserList, setInvitedUserList] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingId, setEditingId] = useState(null); // null = add, id = editing that invite
    const [userEmail, setUserEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [loading, setLoading] = useState(false);
    const [openModalDone, setOpenModalDone] = useState(false);
    const [anotherOfficeValue, setAnotherOfficeValue] = useState(false);

    const onChangeRadio = (e) => {
        setAnotherOfficeValue(e.target.value);
    };

    // Resolve the office's single subscription tier (the office's active tier, or
    // the default Starter tier during onboarding). Cached for the invite payload.
    const resolveOfficeTier = async () => {
        try {
            const res = await controller.officeSubscriptionTier(localStorage.getItem("selectedOffice"));
            const id = res && res.tier && res.tier.id;
            if (id) setOfficeTierId(id);
            return id || null;
        } catch (e) {
            return null;
        }
    };

    const handleDelete = async (e) => {
        setLoading(true);
        const response = await controller.deleteInvitedUser(e.id);
        if (response.status < 250) {
            getInvitedUserList();
            message.success("Invite removed");
        } else {
            message.error("Couldn't remove the invite. Please try again.");
        }
        setLoading(false);
    };

    const handleDoneModal = async () => {
        await controller.addNewOfficeQuestion(anotherOfficeValue ? "yes" : "no");
        props.readOnboardingStatus();
    };

    const showAddModal = () => {
        setEditingId(null);
        setUserEmail("");
        setEmailError("");
        resolveOfficeTier();
        setIsModalVisible(true);
    };

    const showEditModal = (record) => {
        setEditingId(record.id);
        setUserEmail(record.receiver_email || "");
        setEmailError("");
        resolveOfficeTier();
        setIsModalVisible(true);
    };

    // Add a new invite, or (in edit mode) replace one. There's no update endpoint,
    // so editing = send the corrected invite, then remove the old one.
    const handleSubmit = async () => {
        const email = (userEmail || "").trim();
        if (!email) {
            setEmailError("Please enter an email address.");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setEmailError("Please enter a valid email address.");
            return;
        }
        setEmailError("");

        // Editing but the email didn't change — nothing to do.
        if (editingId) {
            const original = (invitedUserList.find((u) => u.id === editingId) || {}).receiver_email;
            if (original && original.trim().toLowerCase() === email.toLowerCase()) {
                handleCancel();
                return;
            }
        }

        setLoading(true);
        try {
            // Every invited user is covered by the office's single subscription —
            // resolve it (no per-user choice). Resolve inline if not cached yet.
            const tierId = officeTierId || (await resolveOfficeTier());
            if (!tierId) {
                message.error("Could not resolve the office subscription. Please try again.");
                setLoading(false);
                return;
            }
            const data = {
                "receiver_email": email,
                "meta_data": {},
                "subscription_tier": tierId,
                "branch": localStorage.getItem("selectedOffice"),
            };
            const response = await controller.inviteUser(data);

            if (response.status < 250) {
                // Replace the old invite only after the new one is safely created.
                if (editingId) {
                    await controller.deleteInvitedUser(editingId);
                }
                message.success(editingId ? "Invite updated" : "Invite sent");
                setIsModalVisible(false);
                setUserEmail("");
                setEditingId(null);
                getInvitedUserList();
            } else {
                // Surface a readable field error, keeping the entered values.
                const msg = response.receiver_email || response.detail || response.error;
                setEmailError(Array.isArray(msg) ? msg[0] : (msg || "Could not send the invite. Please try again."));
            }
        } catch (e) {
            setEmailError("Could not send the invite. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setUserEmail("");
        setEmailError("");
        setEditingId(null);
    };

    const columns = [
        {
            title: 'Email address',
            dataIndex: 'receiver_email',
            key: 'email',
            render: (_, record) => <span className="invite-email">{record.receiver_email}</span>,
        },
        {
            title: '',
            key: 'actions',
            width: 92,
            align: 'right',
            render: (_, record) => (
                <div className="invite-row-actions">
                    <Tooltip title="Edit email">
                        <button type="button" className="invite-action-btn" onClick={() => showEditModal(record)}>
                            <EditOutlined />
                        </button>
                    </Tooltip>
                    <Popconfirm
                        title="Remove this invite?"
                        okText="Remove"
                        cancelText="Cancel"
                        onConfirm={() => handleDelete(record)}
                    >
                        <button type="button" className="invite-action-btn danger" aria-label="Remove invite">
                            <DeleteOutlined />
                        </button>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    const getInvitedUserList = async () => {
        const response = await controller.invitedUserList();
        // Only show invites the current user has SENT for this office (status
        // "inviter"). The endpoint falls back to invites RECEIVED by the user
        // (status "invitee") when they've sent none — those (e.g. the invite used
        // to register, or leftover invites to this email) must NOT show up here as
        // if they'd already been added.
        if (response && response.status === "inviter" && Array.isArray(response.invites)) {
            setInvitedUserList(response.invites);
        } else {
            setInvitedUserList([]);
        }
    };

    // What this registrant was given (CRM Settings switches, frozen at signup):
    // the full screen, only the "another office?" question (multi-office practice
    // registered without team invites), or nothing — then answer "no" and move on.
    const mode = invitesMode(props.plan);

    useEffect(() => {
        if (mode === INVITES_NONE) {
            controller.addNewOfficeQuestion("no").then(() => props.readOnboardingStatus(),
                                                       () => props.readOnboardingStatus());
            return;
        }
        if (mode !== INVITES_OFFICES_ONLY) getInvitedUserList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const handleDoneInviteUser = async () => {
        const response = await controller.checkIsMultiple();
        if (response.result)
            setOpenModalDone(true);
        else {
            await controller.addNewOfficeQuestion("no");
            props.readOnboardingStatus();
        }
    };


    if (mode === INVITES_NONE) return null;

    if (mode === INVITES_OFFICES_ONLY) {
        return (
            <>
                <p className="login-title" style={{ marginBottom: 8 }}>Do you have another office to add?</p>
                <p className="invite-intro">
                    If your practice has more than one location, add the next office now.
                    You can also add offices later from your account.
                </p>
                <Radio.Group
                    onChange={onChangeRadio}
                    value={anotherOfficeValue}
                    style={{ display: 'flex', flexDirection: 'row', gap: 48, margin: '20px 0 8px' }}
                >
                    <Radio value={true}>Yes, add another office</Radio>
                    <Radio value={false}>No, continue</Radio>
                </Radio.Group>
                <div>
                    <Button onClick={handleDoneModal} className='login-button mt15'>
                        Continue
                    </Button>
                </div>
            </>
        );
    }

    return (
        <>
            <p className="login-title" style={{ marginBottom: 8 }}>Invite your team</p>
            <p className="invite-intro">
                Add the people who’ll work in this office — front desk, hygienists,
                associates. Each one gets an email to set up their own login, all
                covered by your office’s subscription.
            </p>

            <div className="custom-table-container">
                <div className="add-message">
                    <Button type="link" onClick={showAddModal} style={{ paddingLeft: 0 }}>
                        <span className="size-16">Add team member</span>
                        <img src={add2} alt="" style={{ marginLeft: '8px' }} />
                    </Button>
                </div>
                <div style={{ width: "100%" }}>
                    <Table
                        rowKey="id"
                        dataSource={invitedUserList}
                        columns={columns}
                        bordered
                        pagination={false}
                        className="custom-table"
                        locale={{ emptyText: "No team members added yet." }}
                    />
                </div>
            </div>

            <p className="invite-optional">
                No one to add right now? You can invite teammates any time later — just continue.
            </p>

            <div>
                <Button onClick={handleDoneInviteUser} className='login-button mt15'>
                    Continue
                </Button>
            </div>

            <Modal
                title="Want to add another Office?"
                visible={openModalDone}
                onCancel={() => setOpenModalDone(false)}
                centered
                footer={[
                    <Button key="done" type="primary" onClick={handleDoneModal} style={{ width: '100%', height: 39 }}>
                        Done
                    </Button>
                ]}
            >
                <Radio.Group onChange={onChangeRadio} value={anotherOfficeValue} style={{ display: 'flex', flexDirection: 'row', marginBottom: 30, marginTop: 25 }}>
                    <Radio value={true} style={{ marginRight: 145 }}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                </Radio.Group>
            </Modal>

            <Modal
                title={editingId ? "Edit invite" : "Invite a team member"}
                visible={isModalVisible}
                onCancel={handleCancel}
                footer={[
                    <Button
                        style={{ width: '100%', height: 39 }}
                        disabled={loading}
                        loading={loading}
                        key="submit"
                        type="primary"
                        onClick={handleSubmit}
                    >
                        {editingId ? "Save" : "Send invite"}
                    </Button>,
                ]}
            >
                <label className='input-lable' style={{ marginTop: 25 }}>Email address</label>
                <Input
                    status={emailError ? "error" : undefined}
                    onChange={(e) => {
                        setUserEmail(e.target.value);
                        if (emailError) setEmailError("");
                    }}
                    name="email"
                    value={userEmail}
                    placeholder='name@yourpractice.com'
                    onPressEnter={handleSubmit}
                />
                {emailError && (
                    <div className="ant-form-item-explain-error" style={{ color: '#ff4d4f', marginTop: 6, fontSize: 13 }}>
                        {emailError}
                    </div>
                )}

                <div style={{ marginTop: 16, fontSize: 12, color: "#888" }}>
                    They’ll get an email to set up their login, covered by your office’s subscription.
                </div>
            </Modal>
        </>
    );
};

export default InviteUsers;
